// Video Director "Add shot" overlay + the card of a shot that has no clip yet (Approve → Generate clip).
// Presentational: DirectorEditStep passes callbacks, VideoDirector owns the API calls.

import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { LuCheck, LuChevronDown, LuFilm, LuImages, LuLibrary, LuUpload, LuWandSparkles } from "react-icons/lu";
import { isStale } from "./edl";
import { OverlayPanel, RefImage, capsOf, effectiveModel, priced, useDraft, useFilePick } from "./DirectorPanels";
import { FrameSlot, ModelSelect, NoEndFrame, RefGrid, SlotPicker, SlotRow, actClass } from "./FrameSlot";
import LibraryPicker from "./LibraryPicker";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);
const tv = (key) => i18n.t(`FileModal.APIProviders.VideoGeneration.${key}`);

const MAX_CAST = 4; // server castIds / refIds cap
const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" };
const smallBtn = { fontSize: 12, padding: "6px 14px", borderRadius: "4px" };
const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "13px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  background: "#fff",
  color: "#1f2937",
  lineHeight: "1.5",
  resize: "vertical",
};

const chip = (on) =>
  `add-video-modal__ai-chip add-video-modal__director-model${on ? " add-video-modal__ai-chip--active" : ""}`;
const readyUrl = (item) => (item?.status === "ready" && item.url ? item.url : null);

const REF_WAIT_MS = 60000; // how long Add shot waits for an uploaded keyframe to become ready

const pick = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

/** Cast / location chips behind one collapsed row that shows the picked names. */
function CastLocation({ cast, locations, castIds, locationId, onCast, onLocation }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const names = [
    ...cast.filter((m) => castIds.includes(m.id)).map((m) => m.name),
    ...locations.filter((m) => m.id === locationId).map((m) => m.name),
  ];
  const thumb = (item) => (
    <span className="add-video-modal__vd-chip-thumb">{readyUrl(item) && <img src={item.url} alt="" />}</span>
  );
  return (
    <div
      ref={ref}
      className="add-video-modal__fs-field"
      onKeyDown={(e) => {
        if (e.key !== "Escape" || !open) return;
        e.stopPropagation();
        setOpen(false);
      }}
    >
      <button
        type="button"
        className="add-video-modal__fs-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>
          <b>{t("CastLocation")}</b> · {names.join(", ") || t("NoneSelected")}
        </span>
        <LuChevronDown size={14} />
      </button>
      {open && (
        <div className="add-video-modal__fs-pop" role="group" aria-label={t("CastLocation")}>
          <div style={labelStyle}>{t("PickCast", { max: MAX_CAST })}</div>
          <div className="add-video-modal__director-models">
            {cast.length === 0 && <span style={{ fontSize: 12, color: "#64748b" }}>{t("NothingHere")}</span>}
            {cast.map((m) => {
              const on = castIds.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  aria-pressed={on}
                  disabled={!on && castIds.length >= MAX_CAST}
                  className={chip(on)}
                  onClick={() => onCast(m.id)}
                >
                  {thumb(m.still)}
                  {m.name}
                </button>
              );
            })}
          </div>
          <div style={labelStyle}>{t("PickLocation")}</div>
          <div className="add-video-modal__director-models">
            {locations.length === 0 && (
              <span style={{ fontSize: 12, color: "#64748b" }}>{t("NothingHere")}</span>
            )}
            {locations.map((m) => {
              const on = locationId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  aria-pressed={on}
                  className={chip(on)}
                  onClick={() => onLocation(on ? null : m.id)}
                >
                  {thumb(m.still)}
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const slotNote = (Icon, text, note) => (
  <div className="add-video-modal__fs-note">
    <Icon size={22} />
    {text && <span>{text}</span>}
    {note && <small>{note}</small>}
  </div>
);

/**
 * POST projects/:id/shots from a description. Start frame slot: empty (text-to-video) | a reference /
 * keyframe / upload | generate from the description (image model, paid). Optional end frame slot (a
 * reference, keyframe, upload or bridge into the next clip; needs a start frame). A start frame can
 * generate the clip at once or only add the shot (free).
 * places = { playhead, end } segment ids the new shot goes after (start is always null);
 * places.next = { playhead, start, end } booleans: a clip follows that insertion point (gates "Bridge into next clip").
 * onSubmit(body) resolves truthy once the server took it.
 */
export function AddShotPanel({
  projectId,
  cast,
  locations,
  refs,
  keyframes,
  places,
  defaultPlace = "playhead",
  prefill,
  models,
  model,
  onModel,
  videoModel,
  onVideoModel,
  aspect = "16:9",
  busy,
  onSubmit,
  onRefUrl,
  onUpload,
  uploading,
  onClose,
  error,
  onDismissError,
}) {
  const initial = {
    prompt: "",
    castIds: [],
    locationId: null,
    refIds: [],
    videoRefIds: [], // Veo reference images of a text-to-video clip
    start: "none", // none (text-to-video) | ref | generate
    existing: null, // start ref: { refId } | { url }
    end: "none", // none | ref | next
    endExisting: null, // end ref: { refId } | { url }
    place: defaultPlace, // playhead | start | end
    ...(prefill?.castIds && { castIds: prefill.castIds }),
    ...(prefill?.locationId && { locationId: prefill.locationId }),
    ...(prefill?.keyframeRefId && {
      start: "ref",
      existing: { refId: prefill.keyframeRefId },
    }),
  };
  const [d, patch, clear, discard, dirty] = useDraft(projectId, "add-shot", initial, !!prefill);
  const [sending, setSending] = useState(false);
  const [waitIds, setWaitIds] = useState(null); // uploaded refs still processing
  const [refFailed, setRefFailed] = useState(false);
  const [picking, setPicking] = useState(null); // start-ref | start-kf | end-ref | end-kf | guide
  const waitBody = useRef(null);
  const uploadTo = useRef("start");
  const [pickFile, fileInput] = useFilePick(async (file) => {
    const id = await onUpload(file);
    if (id) patch(uploadTo.current === "start" ? { start: "ref", existing: { refId: id } } : { end: "ref", endExisting: { refId: id } });
  });
  const readyRefs = refs.filter((r) => readyUrl(r));
  const refName = (r) => t("RefN", { n: refs.indexOf(r) + 1 });
  const refEntries = readyRefs.map((r) => ({ key: `r${r.id}`, item: r, ref: { refId: r.id }, label: refName(r) }));
  const kfEntries = keyframes
    .filter((k) => readyUrl(k.item))
    .map((k) => {
      const same = readyRefs.find((r) => r.url === k.item.url);
      return {
        key: `k${k.id}`,
        item: k.item,
        ref: same ? { refId: same.id } : { url: k.item.url },
        label: t("KeyframeOf", { label: k.label }),
      };
    });
  const generate = d.start === "generate";
  const noStart = d.start === "none" || (d.start === "ref" && !d.existing);
  const caps = capsOf(models, effectiveModel(models, "video", videoModel));
  const nextOk = !!places.next?.[d.place];
  const end = noStart || !caps.endFrame || (d.end === "next" && !nextOk) ? "none" : d.end;
  // Veo reference images replace the start frame (text-to-video only) and are 16:9 only.
  const refsOk = !generate && noStart && caps.maxRefs > 0 && aspect === "16:9" && refs.some((r) => readyUrl(r));
  const vrefs = refsOk
    ? (d.videoRefIds ?? []).filter((id) => readyUrl(refs.find((r) => r.id === id))).slice(0, caps.maxRefs)
    : [];
  const placeId = { playhead: places.playhead, start: null, end: places.end }[d.place];
  const invalid =
    !d.prompt.trim() || (d.start === "ref" && !d.existing) || (end === "ref" && !d.endExisting);

  const send = async (body) => {
    const ok = await onSubmit(body);
    if (!ok) return setSending(false);
    clear();
    return onClose();
  };

  // refs/upload answers before the image is ready and shots rejects a not-ready ref: POST once all are.
  useEffect(() => {
    if (!waitIds) return;
    const rs = waitIds.map((id) => refs.find((x) => x.id === id));
    if (rs.some((r) => r?.status === "failed")) {
      setWaitIds(null);
      setSending(false);
      setRefFailed(true);
    } else if (rs.every((r) => readyUrl(r))) {
      setWaitIds(null);
      send(waitBody.current);
    }
  }, [waitIds, refs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!waitIds) return undefined;
    const timer = setTimeout(() => {
      setWaitIds(null);
      setSending(false);
      setRefFailed(true);
    }, REF_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waitIds]);

  const submit = async (generateClip) => {
    setSending(true);
    setRefFailed(false);
    let body = {
      prompt: d.prompt.trim(),
      castIds: d.castIds,
      locationId: d.locationId,
      placeAfterSegmentId: placeId,
      startMode: d.start,
    };
    const ids = [];
    if (d.start === "ref") {
      const keyframeRefId = d.existing.refId ?? (await onRefUrl(d.existing.url));
      if (!keyframeRefId) return setSending(false);
      body = { ...body, keyframeRefId };
      ids.push(keyframeRefId);
    } else if (generate) {
      body = { ...body, refIds: d.refIds, model: effectiveModel(models, "image", model) };
    }
    if (!generate) body = { ...body, generateClip, videoModel: effectiveModel(models, "video", videoModel) };
    if (generateClip && vrefs.length) body = { ...body, videoRefIds: vrefs };
    if (end === "next") body = { ...body, endFrame: { mode: "next" } };
    if (end === "ref") {
      const refId = d.endExisting.refId ?? (await onRefUrl(d.endExisting.url));
      if (!refId) return setSending(false);
      body = { ...body, endFrame: { mode: "ref", refId } };
      ids.push(refId);
    }
    if (ids.some((id) => !readyUrl(refs.find((r) => r.id === id)))) {
      waitBody.current = body;
      return setWaitIds(ids);
    }
    return send(body);
  };

  const disabled = busy || sending || invalid;
  const prep = !!waitIds;

  // ── slots ──
  const frameOf = (ex) => {
    const r = ex.refId != null ? refs.find((x) => x.id === ex.refId) : null;
    const item = ex.refId != null ? r || { status: "pending" } : { status: "ready", url: ex.url };
    const kf = ex.url ? keyframes.find((k) => k.item?.url === ex.url) : null;
    const sourceLabel = kf ? t("KeyframeOf", { label: kf.label }) : r ? refName(r) : ex.name || t("SrcUpload");
    return {
      sourceLabel,
      visual: (
        <RefImage
          item={item}
          preview={readyUrl(item) ? { items: [{ url: item.url, name: sourceLabel, description: item.prompt }], index: 0 } : null}
        />
      ),
    };
  };
  const imgPrice = models?.image?.[effectiveModel(models, "image", model)];
  const sourcesFor = (slot) => {
    return [
      { id: "ref", label: t("SrcRefSet"), Icon: LuImages, disabled: readyRefs.length === 0, onSelect: () => setPicking(`${slot}-ref`) },
      { id: "kf", label: t("SrcKeyframes"), Icon: LuFilm, disabled: kfEntries.length === 0, onSelect: () => setPicking(`${slot}-kf`) },
      onUpload && {
        id: "up",
        label: t("SrcUpload"),
        Icon: LuUpload,
        disabled: uploading,
        onSelect: () => {
          uploadTo.current = slot;
          pickFile();
        },
      },
      {
        id: "lib",
        label: t("SrcLibrary"),
        Icon: LuLibrary,
        onSelect: () => setPicking(`${slot}-lib`),
      },
      slot === "start"
        ? {
            id: "gen",
            label: t("KeyframeNew"),
            Icon: LuWandSparkles,
            hint: typeof imgPrice === "number" ? t("Models.PriceShort", { n: imgPrice }) : undefined,
            onSelect: () => patch({ start: "generate", existing: null }),
          }
        : nextOk && { id: "next", label: t("BridgeNext"), Icon: LuFilm, onSelect: () => patch({ end: "next", endExisting: null }) },
    ].filter(Boolean);
  };

  let startSlot = {};
  if (d.start === "ref" && d.existing) startSlot = frameOf(d.existing);
  else if (generate)
    startSlot = {
      sourceLabel: "",
      visual: slotNote(LuWandSparkles, t("GeneratedLabel"), t("GeneratedPending")),
    };
  let endSlot = {};
  if (end === "ref" && d.endExisting) endSlot = frameOf(d.endExisting);
  else if (end === "next")
    endSlot = { sourceLabel: t("BridgeLabel"), visual: slotNote(LuFilm, "") };

  const startTo = (entry) => {
    patch({ start: "ref", existing: entry.ref });
    setPicking(null);
  };
  const endTo = (entry) => {
    patch({ end: "ref", endExisting: entry.ref });
    setPicking(null);
  };
  const on = (value) => (e) => !!value && (e.ref.refId ? value.refId === e.ref.refId : value.url === e.ref.url);
  const pickers = {
    "start-ref": [t("StartFrame"), t("SrcRefSet"), refEntries, on(d.existing), startTo],
    "start-kf": [t("StartFrame"), t("SrcKeyframes"), kfEntries, on(d.existing), startTo],
    "end-ref": [t("EndFrame"), t("SrcRefSet"), refEntries, on(d.endExisting), endTo],
    "end-kf": [t("EndFrame"), t("SrcKeyframes"), kfEntries, on(d.endExisting), endTo],
  };
  let picker = null;
  // From library: the url joins the reference set at submit (same wait as a keyframe url), so picking is free.
  const libSlot = picking === "start-lib" ? "start" : picking === "end-lib" ? "end" : null;
  if (libSlot) {
    picker = (
      <SlotPicker
        title={`${libSlot === "start" ? t("StartFrame") : t("EndFrame")} · ${t("SrcLibrary")}`}
        onDone={() => setPicking(null)}
      >
        <LibraryPicker
          kind="image"
          onPick={({ url, name }) => (libSlot === "start" ? startTo : endTo)({ ref: { url, name } })}
        />
      </SlotPicker>
    );
  } else if (picking === "guide" || picking === "vrefs") {
    const [key, sel, max] = picking === "guide" ? ["refIds", d.refIds, MAX_CAST] : ["videoRefIds", vrefs, caps.maxRefs];
    picker = (
      <SlotPicker
        title={t(picking === "guide" ? "GuideImages" : "VideoRefs", { n: sel.length, max })}
        onDone={() => setPicking(null)}
      >
        {readyRefs.length === 0 ? (
          <div style={{ fontSize: 12, color: "#64748b" }}>{t("RegenRefsEmpty")}</div>
        ) : (
          <RefGrid
            entries={refEntries}
            isOn={(e) => sel.includes(e.item.id)}
            onPick={(e) => !(!sel.includes(e.item.id) && sel.length >= max) && patch({ [key]: pick(sel, e.item.id) })}
          />
        )}
      </SlotPicker>
    );
  } else if (pickers[picking]) {
    const [slot, source, entries, isOn, onPick] = pickers[picking];
    picker = (
      <SlotPicker title={`${slot} · ${source}`} onDone={() => setPicking(null)}>
        <RefGrid entries={entries} isOn={isOn} onPick={onPick} />
      </SlotPicker>
    );
  }

  return (
    <OverlayPanel
      className="add-video-modal__vd-overlay-panel--add"
      title={t("AddShot")}
      helper={t("AddShotHelper")}
      onClose={onClose}
      onDiscard={dirty ? discard : undefined}
      error={refFailed ? t("Errors.refs_invalid") : error}
      onDismissError={refFailed ? () => setRefFailed(false) : onDismissError}
      actions={
        <>
          {generate ? (
            <ModelSelect kind="image" value={model} onChange={onModel} models={models} />
          ) : (
            <ModelSelect kind="video" value={videoModel} onChange={onVideoModel} models={models} />
          )}
          <button type="button" className={actClass("primary")} disabled={disabled} onClick={() => submit(true)}>
            {prep
              ? t("PreparingImage")
              : generate
                ? priced(t("GenerateKeyframe"), models, "image", model)
                : priced(t("GenerateClip"), models, "video", videoModel)}
          </button>
          {!generate && (
            <button type="button" className={actClass("ghost")} disabled={disabled} onClick={() => submit(false)}>
              {t("AddShotNoGen")}
            </button>
          )}
          <button type="button" className={actClass("ghost")} onClick={onClose}>
            {tv("Cancel")}
          </button>
        </>
      }
    >
      <SlotRow
        picker={picker}
        start={
          <FrameSlot
            name="start"
            label={t("StartFrame")}
            addLabel={t("AddStartFrame")}
            hint={t("StartEmptyHint")}
            visual={startSlot.visual}
            sourceLabel={startSlot.sourceLabel}
            sources={sourcesFor("start")}
            onRemove={() => patch({ start: "none", existing: null, end: "none", endExisting: null })}
            extra={
              generate && readyRefs.length > 0 ? (
                <button type="button" className="add-video-modal__fs-link" onClick={() => setPicking("guide")}>
                  {t("GuideImages", { n: d.refIds.length, max: MAX_CAST })}
                </button>
              ) : null
            }
          />
        }
        end={
          caps.endFrame ? (
            <FrameSlot
              name="end"
              label={t("EndFrameOptional")}
              addLabel={t("AddEndFrame")}
              hint={noStart ? t("NeedsStart") : t("EndEmptyHint")}
              disabled={noStart}
              visual={endSlot.visual}
              sourceLabel={endSlot.sourceLabel}
              sources={sourcesFor("end")}
              onRemove={() => patch({ end: "none", endExisting: null })}
            />
          ) : (
            <NoEndFrame />
          )
        }
      />
      {refsOk && (
        <button type="button" className="add-video-modal__fs-link" onClick={() => setPicking("vrefs")}>
          {t("VideoRefs", { n: vrefs.length, max: caps.maxRefs })}
        </button>
      )}
      {onUpload && fileInput(t("UploadImage"))}
      <div className="add-video-modal__fs-field">
        <div className="add-video-modal__fs-label">{t("AddShotPrompt")}</div>
        <textarea
          className="add-video-modal__scene-prompt-textarea"
          style={inputStyle}
          rows={3}
          maxLength={2000}
          aria-label={t("AddShotPrompt")}
          value={d.prompt}
          onChange={(e) => patch({ prompt: e.target.value })}
        />
      </div>
      <div className="add-video-modal__fs-opts">
        <CastLocation
          cast={cast}
          locations={locations}
          castIds={d.castIds}
          locationId={d.locationId}
          onCast={(id) => patch({ castIds: pick(d.castIds, id) })}
          onLocation={(id) => patch({ locationId: id })}
        />
        <div className="add-video-modal__fs-field">
          <div role="radiogroup" aria-label={t("Placement")} className="add-video-modal__fs-seg">
            {[
              ["playhead", t("PlaceHere")],
              ["start", t("PlaceStart")],
              ["end", t("PlaceEnd")],
            ].map(([id, text]) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={d.place === id}
                onClick={() => patch({ place: id })}
              >
                {text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </OverlayPanel>
  );
}

/**
 * A shot without a clip: its keyframe, Approve (generated keyframes only; a reference keyframe or a
 * text-only shot goes straight to Generate clip), then Generate clip with the end-frame choice
 * (or the shot's saved endSpec, which the server applies).
 */
export function NewShotCard({ shot, n, edl, refs, models, videoModel, modelPick, aspect = "16:9", busy, onApprove, onGenerate }) {
  const [mode, setMode] = useState("auto"); // auto | next | ref
  const [refId, setRefId] = useState(null);
  const [vrefIds, setVrefIds] = useState([]); // Veo reference images (text-only shots)
  const kf = shot.keyframe;
  const textOnly = shot.startMode === "none";
  const status = textOnly ? "ready" : isStale(kf) ? "failed" : kf.status;
  const approved = textOnly || shot.startMode === "ref" || kf?.approved;
  const spec = shot.endSpec;
  const specRef = spec?.mode === "ref" ? refs.find((r) => r.id === spec.refId) : null;
  const anchor = shot.placeAfterSegmentId;
  // The clip that follows the shot's place: first one at the start, else the one after its anchor.
  const at = anchor === null ? 0 : anchor === undefined ? -1 : edl.findIndex((s) => s.id === anchor) + 1 || -1;
  const hasNext = at >= 0 && at < edl.length;
  const readyRefs = refs.filter((r) => readyUrl(r));
  const refItems = readyRefs.map((r) => ({
    url: r.url,
    name: t("RefN", { n: refs.indexOf(r) + 1 }),
    description: r.prompt,
  }));
  const modes = [
    ["auto", t("EndAuto"), false],
    hasNext && ["next", t("BridgeNext"), false],
    ["ref", t("EndRef"), readyRefs.length === 0],
  ].filter(Boolean);
  const caps = capsOf(models, effectiveModel(models, "video", videoModel));
  const endMode = caps.endFrame ? mode : "auto";
  const endFrame = endMode === "next" ? { mode } : endMode === "ref" ? { mode, refId } : undefined;
  const refsOk = textOnly && caps.maxRefs > 0 && aspect === "16:9" && readyRefs.length > 0;
  const vrefs = refsOk ? vrefIds.filter((id) => readyRefs.some((r) => r.id === id)).slice(0, caps.maxRefs) : [];
  return (
    <div className="add-video-modal__scene-card add-video-modal__vd-newshot">
      <div className="add-video-modal__scene-title">
        <span className="add-video-modal__scene-num">{t("Shot", { n })}</span>
        <span className="add-video-modal__vd-chip">{t("NewShot")}</span>
      </div>
      {(shot.prompt || kf?.prompt) && (
        <div className="add-video-modal__vd-newshot-prompt">{shot.prompt || kf?.prompt}</div>
      )}
      {textOnly ? (
        <span className="add-video-modal__vd-chip">{t("TextOnly")}</span>
      ) : (
        <RefImage item={kf} preview={{ items: [{ url: kf.url, name: t("Shot", { n }), description: kf.prompt }], index: 0 }} />
      )}
      {spec && caps.endFrame && (
        <div className="add-video-modal__scene-field-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {t("EndFrame")}
          {spec.mode === "next" ? (
            <span className="add-video-modal__vd-chip">{t("Bridge")}</span>
          ) : (
            specRef && <RefImage item={specRef} />
          )}
        </div>
      )}
      {status === "ready" && !approved && (
        <button
          type="button"
          className="add-video-modal__btn add-video-modal__btn--primary"
          style={{ ...smallBtn, display: "flex", alignItems: "center", gap: 4 }}
          disabled={busy}
          onClick={() => onApprove(shot.id)}
        >
          <LuCheck size={12} />
          {t("Approve")}
        </button>
      )}
      {status === "ready" && approved && (
        <>
          {!caps.endFrame && !textOnly && <NoEndFrame />}
          {caps.endFrame && !spec && !textOnly && (
            <>
              <div className="add-video-modal__scene-field-label">{t("EndFrame")}</div>
              <div role="radiogroup" aria-label={t("EndFrame")} className="add-video-modal__director-models">
                {modes.map(([id, label, off]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={mode === id}
                    disabled={off}
                    className={chip(mode === id)}
                    onClick={() => setMode(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
          {caps.endFrame && !spec && !textOnly && mode === "ref" && (
            <div className="add-video-modal__director-refs-list">
              {readyRefs.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  aria-pressed={refId === r.id}
                  aria-label={t("RefN", { n: refs.indexOf(r) + 1 })}
                  className={`add-video-modal__director-ref add-video-modal__director-ref--pick${refId === r.id ? " add-video-modal__director-ref--on" : ""}`}
                  onClick={() => setRefId(r.id)}
                >
                  <RefImage item={r} preview={{ items: refItems, index: readyRefs.indexOf(r), click: false }} />
                </button>
              ))}
            </div>
          )}
          {refsOk && (
            <>
              <div className="add-video-modal__scene-field-label">
                {t("VideoRefs", { n: vrefs.length, max: caps.maxRefs })}
              </div>
              <div className="add-video-modal__director-refs-list">
                {readyRefs.map((r) => {
                  const on = vrefs.includes(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      aria-pressed={on}
                      aria-label={t("RefN", { n: refs.indexOf(r) + 1 })}
                      className={`add-video-modal__director-ref add-video-modal__director-ref--pick${on ? " add-video-modal__director-ref--on" : ""}`}
                      onClick={() => !(!on && vrefs.length >= caps.maxRefs) && setVrefIds(pick(vrefs, r.id))}
                    >
                      <RefImage item={r} preview={{ items: refItems, index: readyRefs.indexOf(r), click: false }} />
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {modelPick}
          <button
            type="button"
            className="add-video-modal__btn add-video-modal__btn--primary"
            style={{ ...smallBtn, display: "flex", alignItems: "center", gap: 4 }}
            disabled={busy || (endMode === "ref" && !refId)}
            onClick={() => onGenerate(shot.id, endFrame, vrefs)}
          >
            <LuWandSparkles size={12} />
            {priced(t("GenerateClip"), models, "video", videoModel)}
          </button>
        </>
      )}
    </div>
  );
}
